// ============================================
// useDebugOverlayEvents — E2E test hooks
// ============================================
// Extracted from VoiceBotPanel.tsx in Wave 6b of the widget refactor.
//
// Listens for synthetic CustomEvents that allow Playwright/manual testing
// to open the scheduling/leave popups without going through a real
// conversation. Only attaches listeners when the URL contains
// `?ilandDebugOverlays=1` — never in production traffic.
//
// All actions/refs are passed in via a single `actions` object, kept in a
// ref so the listener never re-attaches when its identity changes (the
// effect deps stay empty and listeners stay attached for the component's
// lifetime, exactly as in the original inline code).

import { MutableRefObject, useEffect, useRef } from 'react'

export interface DebugOverlayActions {
  openSchedulingPopup: (customerName: string | null) => void
  openLeavePopup: () => void
  closePopups: () => void
  /** Wave 0b follow-up: synthetic invocation of handleFunctionCall so
   *  Playwright can exercise extracted tool handlers without WebRTC. */
  callTool?: (name: string, args: Record<string, unknown>, callId: string) => void
}

export const useDebugOverlayEvents = (actions: DebugOverlayActions): void => {
  const actionsRef: MutableRefObject<DebugOverlayActions> = useRef(actions)
  actionsRef.current = actions

  useEffect(() => {
    if (typeof window === 'undefined') return

    const params = new URLSearchParams(window.location.search)
    if (params.get('ilandDebugOverlays') !== '1') return

    const handleOpenScheduling = (event: Event) => {
      const detail = (event as CustomEvent<{ customerName?: string }>).detail
      actionsRef.current.openSchedulingPopup(detail?.customerName || null)
    }
    const handleOpenLeave = () => actionsRef.current.openLeavePopup()
    const handleClosePopups = () => actionsRef.current.closePopups()
    const handleCallTool = (event: Event) => {
      const detail = (event as CustomEvent<{
        name?: string
        args?: Record<string, unknown>
        callId?: string
      }>).detail
      if (!detail?.name) return
      actionsRef.current.callTool?.(
        detail.name,
        detail.args || {},
        detail.callId || `test_call_${Date.now()}`,
      )
    }

    window.addEventListener('iland:test-open-scheduling-popup', handleOpenScheduling as EventListener)
    window.addEventListener('iland:test-open-leave-popup', handleOpenLeave)
    window.addEventListener('iland:test-close-popups', handleClosePopups)
    window.addEventListener('iland:test-call-tool', handleCallTool as EventListener)

    return () => {
      window.removeEventListener('iland:test-open-scheduling-popup', handleOpenScheduling as EventListener)
      window.removeEventListener('iland:test-open-leave-popup', handleOpenLeave)
      window.removeEventListener('iland:test-close-popups', handleClosePopups)
      window.removeEventListener('iland:test-call-tool', handleCallTool as EventListener)
    }
  }, [])
}
